import {
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { ReactNode, useCallback } from "react";
import { DueDiligenceCell } from "./DueDiligenceCell";
import type {
  LabelCountMap,
  RequiredLabels,
  ResourceRow,
} from "./types";

interface Props {
  ariaLabel: string;
  idColumnTitle: string;
  rows: ResourceRow[];
  labels: string[];
  labelCounts: LabelCountMap;
  isLoading: boolean;
  isBuilding: boolean;
  required: RequiredLabels;
  emptyText: string;
  onCellClick: (resource: ResourceRow, label: string, isBuilding: boolean) => void;
  renderRowId: (row: ResourceRow) => ReactNode;
}

const ID_COLUMN_WIDTH = 220;
const LABEL_COLUMN_WIDTH = 180;

// Top-left corner: sticky on BOTH axes (top + left). Highest z so it
// covers both other sticky headers and sticky body cells while scrolling.
const CORNER_HEADER_CLASSES =
  "sticky left-0 top-0 z-40 bg-secondary shadow-[2px_0_4px_rgba(0,0,0,0.04)] w-[220px]";

// Body sticky cells (first column). Solid background so scrolling label
// cells can't bleed through, with a right-edge shadow as the frozen-pane affordance.
const STICKY_CELL_CLASSES =
  "sticky left-0 z-20 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.04)] font-medium w-[220px]";

export const DueDiligenceTable = ({
  ariaLabel,
  idColumnTitle,
  rows,
  labels,
  labelCounts,
  isLoading,
  isBuilding,
  required,
  emptyText,
  onCellClick,
  renderRowId,
}: Props) => {
  const handleCellClick = useCallback(
    (row: ResourceRow, label: string) => {
      onCellClick(row, label, isBuilding);
    },
    [onCellClick, isBuilding],
  );

  const requiredCollection = isBuilding ? required.buildings : required.units;
  const tableMinWidth = ID_COLUMN_WIDTH + LABEL_COLUMN_WIDTH * labels.length;

  return (
    <div
      className="print-expand"
      style={{ ["--dd-min-w" as string]: `${tableMinWidth}px` }}
    >
      <Table
        aria-label={ariaLabel}
        classNames={{
          // HeroUI's wrapper IS the scroll container. Constrain its height so
          // the horizontal scrollbar is always reachable, and remove the
          // default p-4 so position:sticky aligns flush to the visible edge.
          wrapper:
            "border-neutral-secondary-mid/60 max-h-[calc(100vh-260px)] min-h-[420px] overflow-auto rounded-lg border bg-white p-0 shadow-sm",
          table:
            "[min-width:var(--dd-min-w)] w-full border-collapse table-fixed print-table",
          // sticky thead lives in the same scroll container as the body so it
          // tracks horizontal scroll and replaces the old FloatingTableHeaders.
          thead: "[&>tr]:!shadow-none [&>tr]:rounded-none",
          th: "bg-secondary text-neutral-primary border-b-2 border-moss-primary/20 border-r border-neutral-secondary-mid/40 text-xs p-2.5 whitespace-normal break-words hyphens-auto overflow-visible font-semibold sticky top-0 z-30",
          td: "text-neutral-primary border-b border-r border-neutral-secondary-mid/40 p-2.5 text-sm align-top",
          tr: "even:bg-white odd:bg-secondary/15 hover:bg-moss-primary/5 transition-colors",
        }}
      >
        <TableHeader>
          <TableColumn
            key="id"
            className={`text-neutral-primary text-sm break-words ${CORNER_HEADER_CLASSES}`}
          >
            {idColumnTitle}
          </TableColumn>
          {
            labels.map((label) => (
              <TableColumn
                key={label}
                className="bg-secondary text-neutral-primary w-[180px] p-2 text-sm break-words hyphens-auto whitespace-normal"
              >
                <div className="flex flex-col">
                  <span>{label}</span>
                  <span className="text-xs opacity-75">
                    ({labelCounts[label]?.available || 0}/{labelCounts[label]?.total || 0})
                  </span>
                </div>
              </TableColumn>
            )) as unknown as React.ReactElement
          }
        </TableHeader>
        <TableBody
          isLoading={isLoading}
          loadingContent={
            <div className="flex flex-col items-center justify-center gap-3 p-8">
              <Spinner size="lg" color="primary" />
              <p className="text-neutral-secondary-mid text-sm font-medium">
                Documenten laden...
              </p>
            </div>
          }
          emptyContent={
            <div className="text-neutral-primary p-4 text-center">
              {emptyText}
            </div>
          }
          items={rows}
        >
          {(row) => (
            <TableRow key={row.id}>
              <TableCell className={STICKY_CELL_CLASSES}>
                {renderRowId(row)}
              </TableCell>
              {
                labels.map((label) => (
                  <TableCell
                    key={`${row.id}-${label}`}
                    onClick={() => handleCellClick(row, label)}
                    className="hover:bg-moss-primary/10 cursor-pointer transition-colors"
                  >
                    <DueDiligenceCell
                      resource={row}
                      label={label}
                      isBuilding={isBuilding}
                      required={requiredCollection}
                      onCellClick={onCellClick}
                    />
                  </TableCell>
                )) as unknown as React.ReactElement
              }
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
